import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { PageHeader, Card, Badge, Skeleton } from '../components'
import { errorsApi } from '../api'
import type { TeamErrorGroup } from '../api/errors'
import type { ErrorGroupStatus } from '../types'
import { ERROR_GROUP_STATUSES } from '../types'
import { useApps } from '../context/AppsContext'
import { useAuth } from '@roselabs-io/saas-shell'
import {
  AlertTriangle,
  ChevronRight,
  BellOff,
  EyeOff,
  RotateCcw,
  Users,
  Filter,
  RefreshCw,
} from 'lucide-react'
import toast from 'react-hot-toast'

const STATUS_LABELS: Record<ErrorGroupStatus, string> = {
  open: 'Open',
  resolved: 'Resolved',
  ignored: 'Ignored',
  muted: 'Muted',
  regressed: 'Regressed',
}

const STATUS_COLORS: Record<ErrorGroupStatus, string> = {
  open: 'bg-red-100 text-red-700',
  resolved: 'bg-green-100 text-green-700',
  ignored: 'bg-slate-100 text-slate-600',
  muted: 'bg-amber-100 text-amber-700',
  regressed: 'bg-orange-100 text-orange-700',
}

export default function ErrorsPage() {
  const { teamId } = useParams<{ teamId: string }>()
  const { isLoading: authLoading } = useAuth()
  const { apps } = useApps()

  const [errors, setErrors] = useState<TeamErrorGroup[]>([])
  const [total, setTotal] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [isRefreshing, setIsRefreshing] = useState(false)

  // Filters
  const [appFilter, setAppFilter] = useState<string>('')
  const [statusFilter, setStatusFilter] = useState<ErrorGroupStatus | ''>('')
  const [levelFilter, setLevelFilter] = useState<string>('')

  // Pagination
  const [offset, setOffset] = useState(0)
  const limit = 50

  const loadErrors = async (showRefresh = false) => {
    if (authLoading) return

    try {
      if (showRefresh) setIsRefreshing(true)
      else setIsLoading(true)

      const response = await errorsApi.listTeamErrors({
        app_id: appFilter || undefined,
        status: statusFilter || undefined,
        level: levelFilter || undefined,
        offset,
        limit,
      })

      setErrors(response.error_groups)
      setTotal(response.total)
    } catch (err) {
      console.error('Failed to load errors:', err)
      toast.error('Failed to load errors')
    } finally {
      setIsLoading(false)
      setIsRefreshing(false)
    }
  }

  useEffect(() => {
    loadErrors()
  }, [authLoading, appFilter, statusFilter, levelFilter, offset])

  const handleRefresh = () => {
    loadErrors(true)
  }

  const formatTimeAgo = (dateStr: string) => {
    const date = new Date(dateStr)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMins / 60)
    const diffDays = Math.floor(diffHours / 24)

    if (diffMins < 1) return 'just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`
    return date.toLocaleDateString()
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <PageHeader title="Errors" subtitle="All errors across your applications" />
        <Card>
          <div className="space-y-4">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Errors"
        subtitle={`${total} error${total !== 1 ? 's' : ''} across all applications`}
        action={
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        }
      />

      <Card>
        {/* Filters */}
        <div className="flex items-center gap-3 mb-4 pb-4 border-b border-slate-200">
          <Filter className="w-4 h-4 text-slate-400" />
          <select
            value={appFilter}
            onChange={e => {
              setAppFilter(e.target.value)
              setOffset(0)
            }}
            className="text-sm border border-slate-200 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-accent-500"
          >
            <option value="">All Apps</option>
            {apps.map(app => (
              <option key={app.id} value={app.id}>
                {app.name}
              </option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={e => {
              setStatusFilter(e.target.value as ErrorGroupStatus | '')
              setOffset(0)
            }}
            className="text-sm border border-slate-200 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-accent-500"
          >
            <option value="">All Statuses</option>
            {ERROR_GROUP_STATUSES.map(s => (
              <option key={s} value={s}>
                {STATUS_LABELS[s]}
              </option>
            ))}
          </select>
          <select
            value={levelFilter}
            onChange={e => {
              setLevelFilter(e.target.value)
              setOffset(0)
            }}
            className="text-sm border border-slate-200 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-accent-500"
          >
            <option value="">All Levels</option>
            <option value="error">Error</option>
            <option value="warning">Warning</option>
            <option value="info">Info</option>
          </select>
        </div>

        {/* Error List */}
        {errors.length === 0 ? (
          <div className="text-center py-12">
            <AlertTriangle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">
              {appFilter || statusFilter || levelFilter
                ? 'No errors match your filters'
                : 'No errors yet. Errors will appear here once your apps report them.'}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {errors.map(error => (
              <Link
                key={error.id}
                to={`/t/${teamId}/apps/${error.app_id}/errors/${error.id}`}
                className="block bg-slate-50 rounded-lg border border-slate-200 hover:border-accent-300 hover:bg-accent-50/30 transition-colors"
              >
                <div className="flex items-start gap-3 p-3">
                  <ChevronRight className="w-4 h-4 text-slate-400 mt-1 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-slate-900 font-medium truncate">{error.message}</p>
                      {error.status === 'muted' && (
                        <BellOff className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                      )}
                      {error.status === 'ignored' && (
                        <EyeOff className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                      )}
                      {error.status === 'regressed' && (
                        <RotateCcw className="w-3.5 h-3.5 text-orange-500 flex-shrink-0" />
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-500 flex-wrap">
                      <span className="px-1.5 py-0.5 rounded bg-slate-200 text-slate-700 font-medium">
                        {error.app_name}
                      </span>
                      <Badge variant="error">{error.level}</Badge>
                      <span className={`px-1.5 py-0.5 rounded ${STATUS_COLORS[error.status]}`}>
                        {STATUS_LABELS[error.status]}
                      </span>
                      <span>{error.occurrence_count} occurrences</span>
                      {error.users_affected_count > 0 && (
                        <span className="flex items-center gap-1">
                          <Users className="w-3 h-3" />
                          {error.users_affected_count}
                        </span>
                      )}
                      <span className="text-slate-400">{formatTimeAgo(error.last_seen_at)}</span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* Pagination */}
        {total > limit && (
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-200">
            <p className="text-sm text-slate-500">
              Showing {offset + 1} - {Math.min(offset + limit, total)} of {total}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setOffset(Math.max(0, offset - limit))}
                disabled={offset === 0}
                className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setOffset(offset + limit)}
                disabled={offset + limit >= total}
                className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </Card>
    </div>
  )
}
